import { z } from "zod";

/**
 * The single typed shape of the white-label config.
 *
 * Every downstream milestone reads brand/legal/attribution values through
 * this shape — never as literals. See `brand.default.ts` for the only file
 * allowed to contain literal brand strings/hex/domains/emails (enforced by
 * `scripts/lint-brand.ts`).
 */

/**
 * The five white-label legal documents (M11). The keys are the stable
 * document-type identifiers used by the render pipeline, the effective-date
 * map, the consent ledger, and `legalDocVersion` — M7/M12 detect re-acceptance
 * by comparing the accepted version against the current one per type.
 */
export const LEGAL_DOCUMENT_TYPES = ["terms", "privacy", "cookies", "dpa", "acceptableUse"] as const;
export type LegalDocumentType = (typeof LEGAL_DOCUMENT_TYPES)[number];

/** Calendar-date-only ISO string (YYYY-MM-DD) — an effective/"last updated" stamp, not a timestamp. */
const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "must be an ISO calendar date (YYYY-MM-DD)");

/** Per-document effective date — one entry per legal document type. */
export const EffectiveDatesSchema = z.object({
  terms: IsoDateSchema,
  privacy: IsoDateSchema,
  cookies: IsoDateSchema,
  dpa: IsoDateSchema,
  acceptableUse: IsoDateSchema,
});

/**
 * The `legal` block — the compliance/trust half of the white-label config
 * (M11). Consumed by every legal page, the free-tier badge, and the DPA /
 * data-breach templates. Nothing here may be hardcoded downstream.
 */
export const LegalBlockSchema = z.object({
  /** Full registered legal address of the operating entity. */
  registeredAddress: z.string().min(1),
  /** Legal jurisdiction of incorporation/operation (e.g. "Delaware, USA"). */
  jurisdiction: z.string().min(1),
  /** Governing law for the Terms (e.g. "the laws of the State of Delaware"). */
  governingLaw: z.string().min(1),
  /** Privacy / data-protection contact address (may differ from support). */
  privacyContact: z.email(),
  /** The free-tier "Made with X" badge/watermark label. */
  badgeLabel: z.string().min(1),
  /** Per-document effective ("last updated") dates. */
  effectiveDates: EffectiveDatesSchema,
});

/**
 * Per-tool BUILD-GO flags for the M8 deep-analysis tools. Each tool is
 * independently gateable via the white-label config so the wedge ships with all
 * of them OFF; a deployment turns one on (config default or the matching
 * `BRAND_ANALYSIS_*` env override) to expose it. Availability is ALSO gated by
 * the M7 entitlement at open time — this flag only decides presence in the build.
 */
export const ANALYSIS_TOOLS = [
  "radius",
  "territoriesLasso",
  "territoriesAutomated",
  "territoriesBoundary",
  "driveTime",
  "routes",
] as const;
export type AnalysisTool = (typeof ANALYSIS_TOOLS)[number];

export const AnalysisToolFlagsSchema = z.object({
  radius: z.boolean(),
  territoriesLasso: z.boolean(),
  territoriesAutomated: z.boolean(),
  territoriesBoundary: z.boolean(),
  driveTime: z.boolean(),
  routes: z.boolean(),
});
export type AnalysisToolFlags = z.infer<typeof AnalysisToolFlagsSchema>;

/**
 * Per-connector OAuth client credentials (M10 sync). The SHAPE lives in the
 * white-label config; the actual id/secret NEVER live in `brand.default.ts` —
 * they resolve from env (`OAUTH_<CONNECTOR>_CLIENT_ID` / `_CLIENT_SECRET`) at
 * runtime and are `null` until a deployment configures them. The consent-screen
 * app display name + redirect host derive from `brandName` / `appDomain`, so a
 * config swap re-brands the OAuth consent display with no code change. No secret
 * ever appears here or in a compiled artifact.
 */
export const OAuthClientSchema = z.object({
  clientId: z.string().min(1).nullable(),
  clientSecret: z.string().min(1).nullable(),
});
export type OAuthClient = z.infer<typeof OAuthClientSchema>;

/** The M10 connectors that use OAuth (the leaf adapters ship in M10-B). */
export const OAUTH_CONNECTORS = ["gsheet", "salesforce", "hubspot", "zoho"] as const;
export type OAuthConnector = (typeof OAUTH_CONNECTORS)[number];

export const OAuthConfigSchema = z.object({
  gsheet: OAuthClientSchema,
  salesforce: OAuthClientSchema,
  hubspot: OAuthClientSchema,
  zoho: OAuthClientSchema,
});
export type OAuthConfig = z.infer<typeof OAuthConfigSchema>;

/**
 * M13 eval-harness thresholds + cost-rate constants. Every gate baseline
 * (NL plan-success/misinterpretation floors, geocode parity + cache-hit
 * targets, render budgets, per-active-map cost ceiling) and every vendor
 * rate constant lives HERE — no gate baseline is baked into the eval source,
 * so a white-label deployment tunes them without a code change (M13 §Interfaces).
 * The `rates.rateSetDate` is printed on every RunReport so a stale COGS verdict
 * is visible (M13 risk: "cost-accounting drift as vendor rates change").
 */
export const EvalThresholdsSchema = z.object({
  /** NL-mapping & Q&A correctness gates (M13-A). */
  nl: z.object({
    /** Applied-vs-error/undo rate floor over the corpus (M13 AC: >= 80%). */
    planSuccessMin: z.number().min(0).max(1),
    /** Undo-within-10s misinterpretation-proxy ceiling (M13 AC: <= 15%). */
    misinterpretationMax: z.number().min(0).max(1),
    /** Intent-classification accuracy floor over the corpus. */
    intentAccuracyMin: z.number().min(0).max(1),
    /** Per-NL-request blended cost ceiling (USD) — the margin guard. */
    costPerCallCeilingUsd: z.number().nonnegative(),
  }),
  /** Geocode-accuracy gates (authored by M13-B against this same block). */
  geocode: z.object({
    /** Headline US-parity-vs-Google target (%) — the ~95% falsifiable bet. */
    parityTargetPct: z.number().min(0).max(100),
    /** Steady-state shared-cache hit-rate floor (%) — the margin lever. */
    cacheHitFloorPct: z.number().min(0).max(100),
    /** Blended geocode cost-per-1k ceiling (USD) for the default path. */
    costPer1kCeilingUsd: z.number().nonnegative(),
  }),
  /** Render-performance budgets (authored by M13-B against this same block). */
  render: z.object({
    timeToFirstMapP95Ms: z.number().nonnegative(),
    editorLcpMs: z.number().nonnegative(),
    embedLcpMs: z.number().nonnegative(),
    interactionFpsMin: z.number().nonnegative(),
    styleFilterP95Ms: z.number().nonnegative(),
    queryP95Ms: z.number().nonnegative(),
    embedJsGzipMaxBytes: z.number().nonnegative(),
  }),
  /** Estimated tiles + geocode + LLM cost per active map ceiling (USD/mo). */
  costPerActiveMapCeilingUsd: z.number().nonnegative(),
});
export type EvalThresholds = z.infer<typeof EvalThresholdsSchema>;

export const EvalRatesSchema = z.object({
  /** ISO calendar date the rate set was last reviewed (printed on every report). */
  rateSetDate: IsoDateSchema,
  /** LLM input-token rate (USD per 1k input tokens). */
  llmCostPer1kInputTokensUsd: z.number().nonnegative(),
  /** LLM output-token rate (USD per 1k output tokens). */
  llmCostPer1kOutputTokensUsd: z.number().nonnegative(),
  /** Blended OSS-first geocode cost per 1k calls (USD) — ~0 for Census/Pelias. */
  geocodeBlendedCostPer1kUsd: z.number().nonnegative(),
  /** Google-only geocode cost per 1k calls (USD) — the comparison path. */
  geocodeGoogleCostPer1kUsd: z.number().nonnegative(),
});
export type EvalRates = z.infer<typeof EvalRatesSchema>;

export const EvalConfigSchema = z.object({
  thresholds: EvalThresholdsSchema,
  rates: EvalRatesSchema,
});
export type EvalConfig = z.infer<typeof EvalConfigSchema>;

export const BrandConfigSchema = z.object({
  schema_version: z.literal(1),
  brandName: z.string().min(1),
  logoUrl: z.string().min(1),
  colors: z.object({
    primary: z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "colors.primary must be a hex color"),
    accent: z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "colors.accent must be a hex color"),
  }),
  appDomain: z.string().min(1),
  supportEmail: z.email(),
  legalEntity: z.string().min(1),
  legalUrls: z.object({
    tos: z.string().min(1),
    privacy: z.string().min(1),
    cookies: z.string().min(1),
    dpa: z.string().min(1),
    acceptableUse: z.string().min(1),
  }),
  attribution: z.object({
    base: z.string().min(1),
    osm: z.string().min(1),
    protomaps: z.string().min(1),
    // Optional restricted-provider credits — present ONLY when that provider
    // is part of the active tile/geocode stack (M11 attribution + sub-processor
    // rules). Absence means "provider not active", which drives the DPA list.
    maptiler: z.string().min(1).optional(),
    stadia: z.string().min(1).optional(),
    google: z.string().min(1).optional(),
    // Esri World Imagery satellite (MAP-102) — keyless, attribution-required.
    // Present whenever the satellite basemap is offered (it is, by default).
    esri: z.string().min(1).optional(),
  }),
  legal: LegalBlockSchema,
  /** M8 per-tool build-go flags (all false by default — the wedge ships without them). */
  analysisTools: AnalysisToolFlagsSchema,
  /**
   * M9 demographics/census overlay build-go flag (the `demographics_overlays`
   * capability). false by default — the wedge ships without the overlay engine
   * exposed. A deployment turns it on here or via BRAND_DEMOGRAPHICS_OVERLAYS;
   * the M7 entitlement still gates render-time (see @/lib/overlay/gate).
   */
  demographicsOverlays: z.boolean(),
  /**
   * M10 per-connector OAuth client config. Client ids/secrets come from env and
   * default to null (unconfigured) — the connector adapters (M10-B) read these
   * through `@/lib/sync/oauthConfig`, never a hardcoded credential.
   */
  oauth: OAuthConfigSchema,
  /**
   * M13 eval-harness thresholds + cost rates. Every green/red gate baseline the
   * harness reads lives here (no baseline baked into eval source), so a
   * white-label deployment tunes quality gates without a code change.
   */
  eval: EvalConfigSchema,
});

export type BrandConfig = z.infer<typeof BrandConfigSchema>;

/** Deep-partial override shape accepted from env for white-label swaps. */
export const BrandConfigOverrideSchema = BrandConfigSchema.partial({
  schema_version: true,
  brandName: true,
  logoUrl: true,
  appDomain: true,
  supportEmail: true,
  legalEntity: true,
}).extend({
  colors: BrandConfigSchema.shape.colors.partial().optional(),
  legalUrls: BrandConfigSchema.shape.legalUrls.partial().optional(),
  attribution: BrandConfigSchema.shape.attribution.partial().optional(),
  legal: LegalBlockSchema.partial()
    .extend({ effectiveDates: EffectiveDatesSchema.partial().optional() })
    .optional(),
  analysisTools: AnalysisToolFlagsSchema.partial().optional(),
  demographicsOverlays: z.boolean().optional(),
  oauth: OAuthConfigSchema.partial().optional(),
  eval: EvalConfigSchema.optional(),
});

export type BrandConfigOverride = z.infer<typeof BrandConfigOverrideSchema>;
